/**
 * `ProjectWorkflowModel.ts` — web port of Unity's
 * `Models/WorkflowModel/ProjectWorkflowModel.cs`, re-based on the pure-YAML
 * document.
 *
 * Storage-backed single owner of the per-project `workflow.yaml` blob
 * (`account/{customerId}/projectdata/{projectId}/workflow.yaml`) and its
 * derived DynamoDB search summary. Mirrors the `CloudAnnotationsModel`
 * ownership pattern: constructor-injected project + auth + storage, reload on
 * `onActiveProjectChanged`, graceful no-op when there is no cloud context.
 *
 * Web deviations from Unity (deliberate):
 *  - Memory-only loaded document with a debounced upload plus a flush on
 *    `onBeginUnloadProject` (a browser tab loses in-memory state on reload
 *    regardless).
 *  - `importAsync` takes YAML text, not a file path (no browser fs).
 *  - The C# `SemaphoreSlim` document lock becomes an in-flight-load promise
 *    dedupe plus synchronous read-modify-save sections — JS is
 *    single-threaded, but awaits still interleave.
 *
 * Failure honesty (hazards this model exists to prevent):
 *  - A TRANSIENT load failure must not read as "no workflow": the load
 *    attempt is not marked complete, so a later create/patch probes again
 *    instead of template-overwriting a real cloud file.
 *  - An UNREADABLE stored file (YAML that does not parse as a workflow) is a
 *    distinct state ({@link loadError} + {@link unreadableRawText}). Import of
 *    the same bytes adopts that state instead of refusing; the raw editor,
 *    fix queue, and Workflow Builder raw-file response are repair surfaces.
 *    Explicit CREATE still replaces it.
 */

import { randomUuid } from "@models/utils/uuid";
import { Emitter } from "@core/events/emitter";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { IAuthModel } from "@models/auth/IAuthModel";
import type { IStorageModel } from "@models/storage/IStorageModel";
import type { IProductDocsStoreModel } from "@models/docs/IProductDocsStoreModel";
import type { WorkflowSummaryDto } from "@models/data/workflowSummaryDto";
import {
  isProductDocumentArchived,
  normalizeProductNameKey,
  productDocumentDisplayName,
  productDocumentName,
  productDocumentNameKey,
} from "@models/data/productDocumentDto";
import type { WorkflowProductReferenceInput } from "@models/data/workflowProductReferenceDto";
import { buildDisplayNamePathIndex } from "@ai/context/hierarchyPaths";
import type {
  IProjectWorkflowModel,
  WorkflowChangedEvent,
  WorkflowChangeOrigin,
} from "./IProjectWorkflowModel";
import {
  WORKFLOW_FORMAT_VERSION as K_WORKFLOW_FORMAT_VERSION,
  WORKFLOW_TYPES,
  WorkflowDocument,
  WorkflowRunState,
  WorkflowValidationReport,
  validate as validateWorkflow,
  type WorkflowReferenceCatalog,
  type WorkflowKnowledgeDocumentReference,
  type WorkflowProductReference,
  type WorkflowScenePartIndex,
  cloneWorkflowDocument,
  WorkflowUiState,
} from "./WorkflowDocument";
import {
  InvalidWorkflowYamlError,
  applyWorkflowPatch,
  documentFromPatch,
  parseWorkflowDocumentText,
  parseWorkflowPatchText,
  serializeWorkflowDocument,
  type WorkflowYamlIssue,
} from "./WorkflowYaml";
import { applyAuthoredView } from "./WorkflowAuthoredView";
import { buildDefaultWorkflow } from "./WorkflowTemplate";
import {
  K_WORKFLOW_ABSENT,
  presenceOfDocument,
  presenceOfStoredFile,
  setWorkflowPresence,
} from "./workflowPresenceCache";
import {
  buildWorkflowSummary,
  workflowSummarySignature,
} from "./WorkflowSummary";
import type {
  IWorkflowUserStateService,
  WorkflowUserStateIdentity,
} from "./IWorkflowUserStateService";

const K_UPLOAD_DEBOUNCE_MS = 2000;
/** Re-check scene/part refs after a burst of hierarchy edits. */
const K_REFERENCE_CHECK_DEBOUNCE_MS = 300;
/** The only workflow kind so far; stamped when a file does not declare one. */
const K_DEFAULT_WORKFLOW_TYPE = WORKFLOW_TYPES[0];

interface WorkflowProductReferenceSyncArgs {
  readonly previousWorkflowId?: string;
  readonly previousProductNameKey?: string;
}

export class ProjectWorkflowModel implements IProjectWorkflowModel {
  readonly onWorkflowChanged = new Emitter<WorkflowChangedEvent>();
  readonly onValidationChanged = new Emitter<WorkflowValidationReport>();

  private m_loadedDocument: WorkflowDocument | null = null;
  private m_loadedCustomerId: string | null = null;
  private m_loadedProjectId: string | null = null;
  private m_loadedOwnerId: string | null = null;
  /** One real load attempt per project — a not-yet-loaded file must not read as "none". */
  private m_loadAttemptedForProject = false;
  private m_inFlightLoad: Promise<WorkflowDocument | null> | null = null;

  private m_loadIssues: WorkflowYamlIssue[] = [];
  private m_unreadableRawText = "";

  private m_uploadPending = false;
  private m_summaryUploadPending = false;
  private m_lastUploadedSummarySignature: string | null = null;
  private m_uploadTimer: ReturnType<typeof setTimeout> | null = null;
  private m_validationReport: WorkflowValidationReport | null = null;
  private m_referenceCheckTimer: ReturnType<typeof setTimeout> | null = null;
  private m_productReference: WorkflowProductReference | null = null;
  private m_knowledgeDocumentReferences: ReadonlyMap<string, WorkflowKnowledgeDocumentReference> | null = null;
  private m_productReferenceSyncSignature: string | null = null;
  private m_productReferenceSyncChain: Promise<void> = Promise.resolve();

  /** The Workflow panel's debounced-save flush (see IProjectWorkflowModel). */
  private m_pendingEditFlusher: (() => Promise<void>) | null = null;

  private readonly m_offFns: Array<() => void> = [];

  constructor(
    private readonly m_project: IProjectModel,
    private readonly m_auth: IAuthModel,
    private readonly m_storage: IStorageModel,
    private readonly m_userState: IWorkflowUserStateService,
    private readonly m_productDocsStore: IProductDocsStoreModel,
  ) {
    this.m_offFns.push(
      this.m_project.onActiveProjectChanged.on(() => {
        this.resetLoadedState();
        if (this.m_project.activeProject) {
          // Transient load failures throw (so create paths can't mistake them
          // for "no workflow"); this eager warm-up load just swallows them —
          // the next real caller retries.
          void this.getWorkflowAsync().catch(() => {});
        }
      }),
      this.m_project.onBeginUnloadProject.on(() => {
        void this.flushPendingUploadAsync();
      }),
      this.m_project.onScenesListChanged.on(() => this.scheduleReferenceCheck()),
      this.m_project.onLoadSceneCompleted.on(() => this.scheduleReferenceCheck()),
      this.m_project.onEntityChangesApplied.on((event) => {
        if (event.changeKind === "snapshot") return;
        this.scheduleReferenceCheck();
      }),
      this.m_project.onEntityUpdated.on((event) => {
        if (
          event.updateKind === "transform"
          || event.updateKind === "highlight"
          || event.updateKind === "spin"
        ) {
          return;
        }
        this.scheduleReferenceCheck();
      }),
    );
  }

  get loadedWorkflow(): WorkflowDocument | null {
    // Only expose the document while it belongs to the active project (Unity's
    // LoadedWorkflow guards the same way).
    const projectId = this.m_project.activeProject?.id;
    if (!projectId || this.m_loadedProjectId !== projectId) return null;
    return this.m_loadedDocument;
  }

  get hasWorkflowForActiveProject(): boolean {
    return this.loadedWorkflow !== null;
  }

  get loadError(): string | null {
    const issues = this.loadIssues;
    return issues.length === 0 ? null : issues.map((issue) => issue.message).join(" ");
  }

  get loadIssues(): readonly WorkflowYamlIssue[] {
    const projectId = this.m_project.activeProject?.id;
    if (!projectId || this.m_loadedProjectId !== projectId) return [];
    return this.m_loadIssues;
  }

  get unreadableRawText(): string {
    return this.loadError !== null ? this.m_unreadableRawText : "";
  }

  async getWorkflowAsync(signal?: AbortSignal): Promise<WorkflowDocument | null> {
    const ctx = this.cloudContext();
    if (!ctx) return this.loadedWorkflow;

    if (
      this.m_loadAttemptedForProject &&
      this.m_loadedProjectId === ctx.projectId &&
      this.m_loadedCustomerId === ctx.customerId
    ) {
      return this.m_loadedDocument;
    }

    // Dedupe concurrent loads (the web analog of Unity's document lock around EnsureLoaded).
    if (this.m_inFlightLoad) return this.m_inFlightLoad;
    this.m_inFlightLoad = this.loadInternalAsync(ctx.customerId, ctx.projectId, signal);
    try {
      return await this.m_inFlightLoad;
    } finally {
      this.m_inFlightLoad = null;
    }
  }

  async importAsync(rawText: string, signal?: AbortSignal): Promise<WorkflowDocument | null> {
    const ctx = this.requireContext("Cannot import a workflow without an active project.");
    const replacedWorkflowId = this.loadedWorkflow?.uid.trim() ?? "";
    const replacedProductNameKey = normalizeProductNameKey(this.loadedWorkflow?.product ?? "");

    const parsed = parseWorkflowDocumentText(rawText);
    if (parsed.document === null) {
      // Same state a stored unreadable file already uses: keep the bytes, show
      // the issues, do not refuse the import. Canonical serialize would drop
      // the unknown keys and the queue would have nothing left to point at.
      return await this.adoptUnreadableImportAsync(rawText, parsed.issues, ctx, signal);
    }

    const document = parsed.document;
    // An imported file is a new workflow instance for this project. Never
    // adopt its source uid: per-user state and searchable summaries are keyed
    // by workflow id, so carrying it across projects aliases their data.
    document.uid = randomUuid();

    // An import over an unreadable stored file is now a user action (the panel's
    // Upload button), so log the bytes being replaced like the other overwrite
    // paths do — nothing else in the session holds a copy of them.
    this.noteUnreadableReplacement("importAsync");
    const saved = await this.saveDocumentAsync(document, "import", signal);
    if (replacedWorkflowId && replacedWorkflowId !== saved.uid) {
      try {
        await this.m_storage.deleteWorkflowSummary(
          ctx.customerId,
          replacedWorkflowId,
          signal,
        );
      } catch (error) {
        // The imported workflow remains authoritative in S3. A stale search
        // projection must not turn a successful replacement into a failure.
        console.warn(
          `[ProjectWorkflowModel] Failed to remove replaced workflow summary ${replacedWorkflowId}:`,
          error,
        );
      }
      void this.deleteWorkflowProductReferenceByKeyAsync(
        ctx.customerId,
        replacedWorkflowId,
        replacedProductNameKey,
        signal,
      ).catch((error) => {
        console.warn(
          `[ProjectWorkflowModel] Failed to remove replaced workflow product references ${replacedWorkflowId}:`,
          error,
        );
      });
    }
    if (parsed.discardedLegacyWorkflowState) {
      await this.flushPendingUploadAsync();
    }
    return saved;
  }

  async createFromTemplateAsync(signal?: AbortSignal): Promise<WorkflowDocument> {
    this.requireContext("Cannot create a workflow without an active project.");

    // Idempotent: never overwrite an existing READABLE workflow.
    // getWorkflowAsync performs the real cloud load attempt so a not-yet-loaded
    // file is not mistaken for "none" — that guard is the one that matters and
    // it still holds: a transient failure THROWS out of here before any write.
    const existing = await this.getWorkflowAsync(signal);
    if (existing) return existing;
    this.noteUnreadableReplacement("createFromTemplateAsync");

    const document = buildDefaultWorkflow(this.m_project.activeProject?.name);
    document.uid = randomUuid();

    return await this.saveDocumentAsync(document, "import", signal);
  }

  async deleteWorkflowAsync(signal?: AbortSignal): Promise<void> {
    const ctx = this.requireContext("Cannot delete a workflow without an active project.");

    // Read the uid BEFORE anything is torn down — it is the summary row's sort
    // key, and the loaded document is the only place it is held. Deliberately
    // not `getWorkflowAsync`: this is also the way OUT of the unreadable state,
    // where there is no document to load and a probe would only re-fetch the
    // bytes about to be deleted.
    const workflowId = this.m_loadedDocument?.uid?.trim() ?? "";
    const productNameKey = normalizeProductNameKey(this.m_loadedDocument?.product ?? "");

    // Drop the queued upload and never re-arm it. The window this closes is
    // narrow and real: the debounce timer firing DURING the S3 delete below
    // finds `m_loadedDocument` still set (it is nulled after the await) and
    // PUTs the file straight back after the delete lands. Once the document is
    // null `uploadPendingAsync` bails on its own, so this is the only moment
    // the cancel is load-bearing rather than belt-and-braces.
    this.cancelScheduledUpload();
    this.m_uploadPending = false;
    this.m_summaryUploadPending = false;

    // The OBJECT first: it is what every client — and the projects grid's
    // presence probe — reads as "this project has a workflow". A failure here
    // propagates with the local state untouched, so the panel keeps showing a
    // document that still exists rather than reporting a delete that did not
    // happen.
    await this.m_storage.deleteWorkflowYaml(ctx.customerId, ctx.projectId, signal);
    setWorkflowPresence(ctx.customerId, ctx.projectId, K_WORKFLOW_ABSENT);

    if (workflowId.length > 0) {
      try {
        await this.m_storage.deleteWorkflowSummary(ctx.customerId, workflowId, signal);
      } catch (err) {
        // The authoritative file is already gone; a surviving search row is
        // stale metadata, not a workflow. Losing the delete over it would be
        // worse than the orphan.
        console.warn(
          `[ProjectWorkflowModel] Failed to delete workflow summary ${workflowId}:`,
          err,
        );
      }
      try {
        await this.deleteWorkflowProductReferenceByKeyAsync(
          ctx.customerId,
          workflowId,
          productNameKey,
          signal,
        );
      } catch (err) {
        console.warn(
          `[ProjectWorkflowModel] Failed to delete workflow product references ${workflowId}:`,
          err,
        );
      }
    }

    this.m_loadedDocument = null;
    this.m_productReference = null;
    this.m_knowledgeDocumentReferences = null;
    this.m_productReferenceSyncSignature = null;
    this.m_loadIssues = [];
    this.m_unreadableRawText = "";
    this.m_lastUploadedSummarySignature = null;
    // The load stays ATTEMPTED and the project ids stay bound: the answer for
    // this project is now a known "none", so a later create must not re-probe
    // S3 for the object it just removed.
    this.m_loadedCustomerId = ctx.customerId;
    this.m_loadedProjectId = ctx.projectId;
    this.m_loadAttemptedForProject = true;

    this.onWorkflowChanged.emit({
      projectId: ctx.projectId,
      document: null,
      origin: "unloaded",
    });
    this.refreshValidation();
  }

  async saveWorkflowUiAsync(ui: WorkflowUiState | null): Promise<void> {
    // The editor's debounce holds the author's keystrokes in a closure. Flush
    // BEFORE reading the document, or a layout save landing inside that window
    // persists the pre-edit text and reverts what they just typed.
    await this.flushPendingEditsAsync();

    const current = this.m_loadedDocument;
    // No document, or bytes that do not parse: there is nothing to attach a
    // layout to, and writing one would mean replacing a file we cannot read.
    // A dropped layout costs a panel position; the alternative costs the file.
    if (current === null) return;

    const next = cloneWorkflowDocument(current);
    next.workflowUi = ui !== null && !ui.isEmpty ? ui : null;

    // Nothing to do when the section is already exactly this — a run that ends
    // where it started must not re-upload the file or bump its timestamps.
    if (serializeWorkflowDocument(next) === serializeWorkflowDocument(current)) return;

    await this.saveDocumentAsync(next, "layout");
  }

  async saveDocumentAsync(
    document: WorkflowDocument,
    origin: WorkflowChangeOrigin,
    signal?: AbortSignal,
  ): Promise<WorkflowDocument> {
    const ctx = this.requireContext("Cannot save a workflow without an active project.");
    const previousWorkflowId = this.m_loadedDocument?.uid?.trim() ?? "";
    const previousProductNameKey = normalizeProductNameKey(this.m_loadedDocument?.product ?? "");

    // Identity is stamped on EVERY save so no edit path can sever the binding.
    document.projectId = ctx.projectId;
    document.projectName = this.m_project.activeProject?.name?.trim() ?? "";
    if (document.uid.trim().length === 0) document.uid = randomUuid();
    if (document.type.trim().length === 0) document.type = K_DEFAULT_WORKFLOW_TYPE;
    // Only when ABSENT: a version is frozen once anything has emitted or
    // consumed it, so an existing file keeps the version it was written
    // against rather than silently claiming conformance to a newer format.
    if (document.version.trim().length === 0) document.version = K_WORKFLOW_FORMAT_VERSION;
    this.m_productReference = await this.loadProductReferenceAsync(
      ctx.customerId,
      document.product,
      signal,
    );
    this.m_knowledgeDocumentReferences = await this.loadKnowledgeDocumentReferencesAsync(
      ctx.customerId,
      document,
      signal,
    );

    this.m_loadedDocument = document;
    this.m_loadedCustomerId = ctx.customerId;
    this.m_loadedProjectId = ctx.projectId;
    this.m_loadedOwnerId = this.m_project.activeProject?.creatorId?.trim() ?? "";
    this.m_loadAttemptedForProject = true;
    this.m_loadIssues = [];
    this.m_unreadableRawText = "";

    this.m_uploadPending = true;
    this.queueSummaryIfChanged(document);
    this.scheduleDebouncedUpload();
    setWorkflowPresence(ctx.customerId, ctx.projectId, presenceOfDocument(document));
    this.onWorkflowChanged.emit({ projectId: ctx.projectId, document, origin });
    this.refreshValidation();
    this.scheduleWorkflowProductReferenceSync({
      previousWorkflowId,
      previousProductNameKey,
    });
    return document;
  }

  async saveYamlTextAsync(
    rawYamlText: string,
    origin: WorkflowChangeOrigin,
    signal?: AbortSignal,
  ): Promise<WorkflowDocument> {
    this.requireContext("Cannot save a workflow without an active project.");

    const parsed = parseWorkflowDocumentText(rawYamlText);
    if (parsed.document === null) {
      throw new InvalidWorkflowYamlError(parsed.issues);
    }

    // Keep uid continuity when the raw edit dropped it (identity re-stamp
    // happens in saveDocumentAsync).
    if (parsed.document.uid.trim().length === 0) {
      const existingUid = this.m_loadedDocument?.uid ?? "";
      if (existingUid.length > 0) parsed.document.uid = existingUid;
    }

    return await this.saveDocumentAsync(parsed.document, origin, signal);
  }

  async saveAuthoredViewAsync(
    editedText: string,
    origin: WorkflowChangeOrigin,
    signal?: AbortSignal,
  ): Promise<WorkflowDocument> {
    this.requireContext("Cannot save a workflow without an active project.");
    const base = await this.getWorkflowAsync(signal);
    if (!base) {
      this.refuseWhenUnreadable();
      throw new Error("No workflow is loaded for the active project.");
    }

    const document = applyAuthoredView(base, editedText);
    return await this.saveDocumentAsync(document, origin, signal);
  }

  async getWorkflowStateAsync(signal?: AbortSignal): Promise<WorkflowRunState | null> {
    const identity = await this.workflowStateIdentityAsync(signal);
    const state = await this.m_userState.getAsync(identity, signal);
    if (!this.isCurrentWorkflowStateIdentity(identity)) {
      throw new Error(
        "[ProjectWorkflowModel] Active user, project, or workflow changed while state was loading.",
      );
    }
    return state;
  }

  async updateWorkflowStateAsync(
    state: WorkflowRunState,
    signal?: AbortSignal,
  ): Promise<void> {
    const identity = await this.workflowStateIdentityAsync(signal);
    await this.m_userState.putAsync(identity, state, signal);
  }

  async applyYamlAsync(
    yamlText: string,
    origin: WorkflowChangeOrigin,
    signal?: AbortSignal,
  ): Promise<WorkflowDocument> {
    // Message wording matters: `Workflow_Executor` maps the substring
    // "without an active project" onto `error: no_workflow_for_project`.
    this.requireContext("Cannot update a workflow without an active project.");

    const parsed = parseWorkflowPatchText(yamlText);
    if (parsed.doc === null) {
      throw new InvalidWorkflowYamlError(parsed.issues);
    }

    // Probe for an existing file through getWorkflowAsync, not `loadedWorkflow`
    // — the latter returns null for both "no file" and "not fetched yet", and
    // creating on the second would overwrite a real cloud file.
    const existing = await this.getWorkflowAsync(signal);

    if (existing === null) {
      this.noteUnreadableReplacement("applyYamlAsync");
      const document = documentFromPatch(parsed.doc);
      document.uid = randomUuid();
      return await this.saveDocumentAsync(document, origin, signal);
    }

    // Re-read AFTER the await and apply synchronously from there to the single
    // save so an editor change that landed during the read is not discarded.
    const document = this.m_loadedDocument ?? existing;
    const result = applyWorkflowPatch(document, parsed.doc);
    if (result.errors.length > 0) {
      throw new InvalidWorkflowYamlError(
        result.errors.map((message) => ({ message, line: null, column: null })),
      );
    }

    if (!result.changed) return document;

    return await this.saveDocumentAsync(result.document, origin, signal);
  }

  getWorkflowYamlText(): string {
    const document = this.loadedWorkflow;
    return document === null ? "" : serializeWorkflowDocument(document);
  }

  validateLoadedWorkflow(): WorkflowValidationReport {
    const document = this.loadedWorkflow;
    if (document) {
      const report = validateWorkflow(document, this.buildReferenceCatalog());
      this.m_validationReport = report;
      return report;
    }

    const report = new WorkflowValidationReport();
    report.errors.push(
      this.loadError !== null
        ? `The stored workflow file could not be read: ${this.loadError}`
        : "No workflow is loaded for the active project.",
    );
    this.m_validationReport = report;
    return report;
  }

  checkReferences(document: WorkflowDocument): WorkflowValidationReport {
    return validateWorkflow(document, this.buildReferenceCatalog());
  }

  get lastValidationReport(): WorkflowValidationReport {
    return this.m_validationReport ?? this.validateLoadedWorkflow();
  }

  async flushPendingUploadAsync(): Promise<void> {
    this.cancelScheduledUpload();
    await this.uploadPendingAsync();
  }

  setPendingEditFlusher(flusher: (() => Promise<void>) | null): void {
    this.m_pendingEditFlusher = flusher;
  }

  async flushPendingEditsAsync(): Promise<void> {
    const flusher = this.m_pendingEditFlusher;
    if (flusher === null) return;
    try {
      await flusher();
    } catch (e) {
      // The flush writes through the same save path the debounce would have
      // used; a failure there is already surfaced to the editor — the agent
      // call proceeds against the last saved state rather than failing.
      console.warn("[ProjectWorkflowModel] pending-edit flush failed:", e);
    }
  }

  dispose(): void {
    this.cancelScheduledUpload();
    this.cancelReferenceCheck();
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
  }

  // ---- Internals -------------------------------------------------

  private async loadInternalAsync(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<WorkflowDocument | null> {
    let document: WorkflowDocument | null = null;
    let rawText: string | null = null;
    let discardedLegacyWorkflowState = false;
    try {
      rawText = await this.m_storage.getWorkflowYaml(customerId, projectId, signal);
    } catch (err) {
      // A transient failure must NOT read as "no workflow": the load stays
      // un-attempted (the next probe retries) and the error PROPAGATES so a
      // create path can't mistake the failure for "none exists" and
      // template-overwrite a real cloud file.
      console.warn(`[ProjectWorkflowModel] Failed to load workflow for ${projectId}:`, err);
      throw err;
    }

    this.m_loadedCustomerId = customerId;
    this.m_loadedProjectId = projectId;
    this.m_loadedOwnerId = this.m_project.activeProject?.creatorId?.trim() ?? "";
    this.m_loadAttemptedForProject = true;
    this.m_loadIssues = [];
    this.m_unreadableRawText = "";

    if (rawText !== null && rawText.trim().length > 0) {
      const parsed = parseWorkflowDocumentText(rawText);
      if (parsed.document !== null) {
        document = parsed.document;
        discardedLegacyWorkflowState = parsed.discardedLegacyWorkflowState;
      } else {
        // Readable bytes, unreadable document. Keep the raw text for the panel's
        // forced repair surface and the Workflow Builder's get_workflow result.
        this.m_loadIssues = [...parsed.issues];
        this.m_unreadableRawText = rawText;
        console.warn(
          `[ProjectWorkflowModel] Stored workflow for ${projectId} is unreadable: ${this.loadError}`,
        );
      }
    }

    this.m_productReference = await this.loadProductReferenceAsync(customerId, document?.product, signal);
    this.m_knowledgeDocumentReferences = await this.loadKnowledgeDocumentReferencesAsync(
      customerId,
      document,
      signal,
    );
    this.m_loadedDocument = document;
    // Whatever the grid's badge probe would have found, learned for free: this
    // is the same GET, and a project just opened is the one most likely to be
    // looked at again on the way back.
    setWorkflowPresence(customerId, projectId, presenceOfStoredFile(rawText, document));

    if (document && discardedLegacyWorkflowState) {
      try {
        await this.m_storage.putWorkflowYaml(
          customerId,
          projectId,
          serializeWorkflowDocument(document),
          signal,
        );
      } catch (error) {
        // The loaded model is already clean. Keep the canonical rewrite pending
        // so the normal upload retry path can remove the retired key later.
        this.m_uploadPending = true;
        console.warn(
          `[ProjectWorkflowModel] Failed to remove legacy workflow_state for ${projectId}:`,
          error,
        );
      }
    }

    // Emit even when no document parsed (missing or unreadable). A confirmed
    // Viewer has no editor fallback: the run model needs the settled null to
    // report that automatic playback cannot start and let the project route
    // show the standard failure alert.
    this.onWorkflowChanged.emit({ projectId, document, origin: "load" });
    if (document) {
      this.refreshValidation();
      this.scheduleWorkflowProductReferenceSync();
      // The YAML was read successfully, so an idempotent PutItem can safely
      // backfill or refresh its searchable Dynamo projection.
      this.m_summaryUploadPending = true;
      this.scheduleDebouncedUpload();
    }

    return document;
  }

  /**
   * Persists the imported bytes as-is and adopts the unreadable-file state so
   * the panel can show the text and its parse issues. The debounce upload path
   * only serializes a loaded document, so this write is immediate.
   */
  private async adoptUnreadableImportAsync(
    rawText: string,
    issues: readonly WorkflowYamlIssue[],
    ctx: { customerId: string; projectId: string },
    signal?: AbortSignal,
  ): Promise<null> {
    this.noteUnreadableReplacement("importAsync");
    this.cancelScheduledUpload();
    this.m_uploadPending = false;
    this.m_summaryUploadPending = false;
    await this.m_storage.putWorkflowYaml(ctx.customerId, ctx.projectId, rawText, signal);
    setWorkflowPresence(ctx.customerId, ctx.projectId, presenceOfStoredFile(rawText, null));
    this.m_loadedDocument = null;
    this.m_productReference = null;
    this.m_knowledgeDocumentReferences = null;
    this.m_loadedCustomerId = ctx.customerId;
    this.m_loadedProjectId = ctx.projectId;
    this.m_loadAttemptedForProject = true;
    this.m_loadIssues = [...issues];
    this.m_unreadableRawText = rawText;

    this.onWorkflowChanged.emit({
      projectId: ctx.projectId,
      document: null,
      origin: "import",
    });
    return null;
  }

  /**
   * A CREATE over an unreadable stored file is allowed (user direction,
   * 2026-08-10) — it used to refuse, which left a real dead end: an old-format
   * file makes `getWorkflowAsync` return null with {@link loadError} set, so
   * the agent's `update_workflow` create and `get_workflow` report all refused,
   * while the only repair surfaces (the raw editor and Clear) sat behind debug
   * mode. Since this format keeps no backward compatibility, "the stored file
   * no longer parses" is the EXPECTED end state of a format change and
   * reseeding is its documented repair. The panel itself no longer auto-seeds
   * over that state (2026-08-12) — opening it shows the issues instead.
   *
   * The guard that actually protects data is untouched: a transient load
   * failure THROWS out of `getWorkflowAsync`, so a create can still never fire
   * on "could not check". This one only ever covered bytes we DID read and
   * could not parse — and those bytes are logged in full here, because after
   * the overwrite nothing else in the session holds a copy.
   */
  private noteUnreadableReplacement(where: string): void {
    const loadError = this.loadError;
    if (loadError === null) return;
    console.warn(
      `[ProjectWorkflowModel] ${where}: replacing an unreadable stored workflow ` +
        `(${loadError}). The replaced text follows so it stays recoverable ` +
        `from this console:\n${this.m_unreadableRawText}`,
    );
  }

  /**
   * Still refuses on the SAVE paths: an editor save is not a create, and the
   * WYSIWYG projection is empty for an unreadable file, so writing from it
   * would replace the broken text with whatever that empty view produced. The
   * panel forces raw mode, but this remains a model-level safety belt.
   */
  private refuseWhenUnreadable(): void {
    const loadError = this.loadError;
    if (loadError !== null) {
      throw new Error(
        `invalid_workflow_yaml: the stored workflow file could not be read (${loadError}); repair it in the raw YAML editor or through update_workflow's full-file repair mode instead of saving over it from an empty authored view.`,
      );
    }
  }

  private scheduleDebouncedUpload(): void {
    if (this.m_uploadTimer !== null) clearTimeout(this.m_uploadTimer);
    this.m_uploadTimer = setTimeout(() => {
      this.m_uploadTimer = null;
      void this.uploadPendingAsync();
    }, K_UPLOAD_DEBOUNCE_MS);
  }

  private cancelScheduledUpload(): void {
    if (this.m_uploadTimer !== null) {
      clearTimeout(this.m_uploadTimer);
      this.m_uploadTimer = null;
    }
  }

  private async uploadPendingAsync(): Promise<void> {
    if (!this.m_uploadPending && !this.m_summaryUploadPending) return;
    const document = this.m_loadedDocument;
    const customerId = this.m_loadedCustomerId;
    const projectId = this.m_loadedProjectId;
    const ownerId = this.m_loadedOwnerId;
    if (!document || !customerId || !projectId) {
      this.m_uploadPending = false;
      this.m_summaryUploadPending = false;
      return;
    }

    if (this.m_uploadPending) {
      // The serialized text is captured synchronously; a save that lands
      // mid-upload just re-marks pending.
      this.m_uploadPending = false;
      try {
        await this.m_storage.putWorkflowYaml(
          customerId,
          projectId,
          serializeWorkflowDocument(document),
        );
      } catch (err) {
        // Keep both writes pending: the derived row must never get ahead of
        // its authoritative YAML.
        this.m_uploadPending = true;
        console.warn(`[ProjectWorkflowModel] Workflow upload failed for ${projectId}:`, err);
        return;
      }
    }

    // A newer save landed while S3 was in flight. Let its next upload publish
    // YAML before deriving a Dynamo row from that newer in-memory document.
    if (this.m_uploadPending || !this.m_summaryUploadPending) return;

    let summary: WorkflowSummaryDto;
    let signature: string;
    try {
      summary = buildWorkflowSummary(this.m_loadedDocument ?? document, ownerId ?? "");
      signature = workflowSummarySignature(summary);
    } catch (err) {
      this.m_summaryUploadPending = true;
      console.warn(`[ProjectWorkflowModel] Workflow summary is not writable for ${projectId}:`, err);
      return;
    }

    this.m_summaryUploadPending = false;
    try {
      await this.m_storage.putWorkflowSummary(customerId, summary);
      this.m_lastUploadedSummarySignature = signature;
    } catch (err) {
      this.m_summaryUploadPending = true;
      console.warn(`[ProjectWorkflowModel] Workflow summary upload failed for ${projectId}:`, err);
    }
  }

  private queueSummaryIfChanged(document: WorkflowDocument): void {
    try {
      const summary = buildWorkflowSummary(document, this.m_loadedOwnerId ?? "");
      if (workflowSummarySignature(summary) !== this.m_lastUploadedSummarySignature) {
        this.m_summaryUploadPending = true;
      }
    } catch {
      // Keep retrying through the normal upload path, which reports the
      // actionable missing workflow or project-owner id error.
      this.m_summaryUploadPending = true;
    }
  }

  private resetLoadedState(): void {
    this.cancelScheduledUpload();
    this.cancelReferenceCheck();
    const hadDocument = this.m_loadedDocument !== null;
    const hadUnreadable = this.m_loadIssues.length > 0;
    const previousProjectId = this.m_loadedProjectId;
    this.m_loadedDocument = null;
    this.m_loadedCustomerId = null;
    this.m_loadedProjectId = null;
    this.m_loadedOwnerId = null;
    this.m_productReference = null;
    this.m_knowledgeDocumentReferences = null;
    this.m_productReferenceSyncSignature = null;
    this.m_loadAttemptedForProject = false;
    this.m_loadIssues = [];
    this.m_unreadableRawText = "";
    this.m_uploadPending = false;
    this.m_summaryUploadPending = false;
    this.m_lastUploadedSummarySignature = null;
    this.m_validationReport = null;
    if ((hadDocument || hadUnreadable) && previousProjectId) {
      this.onWorkflowChanged.emit({
        projectId: previousProjectId,
        document: null,
        origin: "unloaded",
      });
    }
  }

  private scheduleReferenceCheck(): void {
    if (this.m_referenceCheckTimer !== null) clearTimeout(this.m_referenceCheckTimer);
    this.m_referenceCheckTimer = setTimeout(() => {
      this.m_referenceCheckTimer = null;
      this.refreshValidation();
    }, K_REFERENCE_CHECK_DEBOUNCE_MS);
  }

  private cancelReferenceCheck(): void {
    if (this.m_referenceCheckTimer !== null) {
      clearTimeout(this.m_referenceCheckTimer);
      this.m_referenceCheckTimer = null;
    }
  }

  /**
   * Recompute the cached report and notify the panel. Purely derived: broken
   * references are warnings the panel's queue shows and the builder resolves —
   * revalidating never writes the file back (the `ready` -> `needs-sync`
   * auto-downgrade went with the status key).
   */
  private refreshValidation(): void {
    const report = this.validateLoadedWorkflow();
    this.onValidationChanged.emit(report);
  }

  private buildReferenceCatalog(): WorkflowReferenceCatalog {
    return {
      ...buildWorkflowReferenceCatalog(this.m_project),
      product: this.m_productReference,
      knowledgeDocs: this.m_knowledgeDocumentReferences,
    };
  }

  private async loadProductReferenceAsync(
    customerId: string,
    product: string | null | undefined,
    signal?: AbortSignal,
  ): Promise<WorkflowProductReference | null> {
    const productKey = normalizeProductNameKey(product ?? "");
    if (!customerId || !productKey) return null;

    return await this.m_productDocsStore.getProductStatusAsync(
      { customerId, productKey },
      signal,
    );
  }

  private async loadKnowledgeDocumentReferencesAsync(
    customerId: string,
    document: WorkflowDocument | null | undefined,
    signal?: AbortSignal,
  ): Promise<ReadonlyMap<string, WorkflowKnowledgeDocumentReference> | null> {
    const documentNameKeys = uniqueWorkflowKnowledgeDocumentKeys(document);
    if (!customerId || documentNameKeys.length === 0) return null;

    const statuses = new Map<string, WorkflowKnowledgeDocumentReference>();
    for (const documentNameKey of documentNameKeys) {
      statuses.set(documentNameKey, {
        documentNameKey,
        state: "missing",
        displayName: documentNameKey,
      });
    }

    try {
      const rows = await this.m_storage.getDocumentsByNameKeys(
        customerId,
        documentNameKeys,
        { includeArchived: true },
        signal,
      );
      for (const row of rows) {
        const documentNameKey = productDocumentNameKey(row);
        if (!documentNameKey || !statuses.has(documentNameKey)) continue;
        statuses.set(documentNameKey, {
          documentNameKey,
          state: isProductDocumentArchived(row) ? "archived" : "found",
          displayName:
            productDocumentDisplayName(row) || productDocumentName(row) || documentNameKey,
        });
      }
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      for (const documentNameKey of documentNameKeys) {
        statuses.set(documentNameKey, {
          documentNameKey,
          state: "unavailable",
          displayName: documentNameKey,
          reason,
        });
      }
    }

    return statuses;
  }

  private scheduleWorkflowProductReferenceSync(args: WorkflowProductReferenceSyncArgs = {}): void {
    const document = this.m_loadedDocument;
    const customerId = this.m_loadedCustomerId?.trim() ?? "";
    if (!this.m_storage.isOnline || !document || !customerId) return;

    const workflowId = document.uid.trim();
    if (!workflowId) return;

    const productNameKey = normalizeProductNameKey(document.product ?? "");
    const previousWorkflowId = args.previousWorkflowId?.trim() || workflowId;
    const previousProductNameKey = normalizeProductNameKey(args.previousProductNameKey ?? "");
    const status = this.m_productReference;
    const statusMatchesProduct =
      !!status && normalizeProductNameKey(status.productKey) === productNameKey;

    const productLookupUnavailable =
      productNameKey.length > 0 && (!statusMatchesProduct || status?.state === "unavailable");
    const validProduct =
      !productLookupUnavailable && productNameKey && status?.state === "found" ? status : null;
    const workflowName = document.workflowName.trim();
    const workflowVersion = document.version.trim();
    const validatedBy = this.m_auth.currentUser?.userId?.trim() ?? "";
    const deletePreviousProductNameKey =
      previousProductNameKey
      && (
        previousWorkflowId !== workflowId
        || previousProductNameKey !== (validProduct?.productKey ?? productNameKey)
        || (!validProduct && !productLookupUnavailable)
      )
        ? previousProductNameKey
        : "";
    const signature = [
      customerId,
      workflowId,
      validProduct?.productKey ?? "",
      validProduct?.displayName ?? "",
      productLookupUnavailable ? productNameKey : "",
      previousWorkflowId,
      deletePreviousProductNameKey,
      workflowName,
      workflowVersion,
      validatedBy,
    ].join("\n");
    if (signature === this.m_productReferenceSyncSignature) return;

    this.m_productReferenceSyncChain = this.m_productReferenceSyncChain
      .catch(() => {
        // Keep the queue alive after a failed sync; the failure is logged below.
      })
      .then(async () => {
        if (validProduct) {
          const reference: WorkflowProductReferenceInput = {
            productName: validProduct.displayName || document.product?.trim() || validProduct.productKey,
            productNameKey: validProduct.productKey,
            workflowId,
            workflowName: workflowName || undefined,
            workflowVersion: workflowVersion || undefined,
            validatedBy: validatedBy || undefined,
          };
          await this.m_storage.putWorkflowProductReference(customerId, reference);
        }
        await this.deleteWorkflowProductReferenceByKeyAsync(
          customerId,
          previousWorkflowId,
          deletePreviousProductNameKey,
        );
        this.m_productReferenceSyncSignature = signature;
      })
      .catch((error) => {
        console.warn(
          `[ProjectWorkflowModel] Failed to sync workflow product reference ${workflowId}:`,
          error,
        );
      });
  }

  private async deleteWorkflowProductReferenceByKeyAsync(
    customerId: string,
    workflowId: string,
    productNameKey: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const normalizedProductNameKey = normalizeProductNameKey(productNameKey);
    const normalizedWorkflowId = workflowId.trim();
    if (!customerId || !normalizedWorkflowId || !normalizedProductNameKey) return;
    await this.m_storage.deleteWorkflowProductReference(
      customerId,
      normalizedProductNameKey,
      normalizedWorkflowId,
      signal,
    );
  }

  private cloudContext(): { customerId: string; projectId: string } | null {
    if (!this.m_storage.isOnline) return null;
    const customerId = this.m_auth.currentUser?.customerId;
    const projectId = this.m_project.activeProject?.id;
    if (!customerId || !projectId) return null;
    return { customerId, projectId };
  }

  private requireContext(message: string): { customerId: string; projectId: string } {
    const ctx = this.cloudContext();
    if (!ctx) throw new Error(message);
    return ctx;
  }

  private async workflowStateIdentityAsync(
    signal?: AbortSignal,
  ): Promise<WorkflowUserStateIdentity> {
    const ctx = this.requireContext(
      "Cannot access workflow state without an authenticated active project.",
    );
    const userId = this.m_auth.currentUser?.userId?.trim() ?? "";
    if (!userId) {
      throw new Error("Cannot access workflow state without an authenticated user.");
    }

    const loaded = await this.getWorkflowAsync(signal);
    const document = this.m_loadedDocument ?? loaded;
    if (!document || document.projectId !== ctx.projectId || document.uid.trim().length === 0) {
      throw new Error("No workflow is loaded for the active project.");
    }

    return {
      accountId: ctx.customerId,
      userId,
      projectId: ctx.projectId,
      workflowId: document.uid.trim(),
    };
  }

  private isCurrentWorkflowStateIdentity(identity: WorkflowUserStateIdentity): boolean {
    const ctx = this.cloudContext();
    const userId = this.m_auth.currentUser?.userId?.trim() ?? "";
    const document = this.loadedWorkflow;
    return (
      ctx?.customerId === identity.accountId
      && ctx.projectId === identity.projectId
      && userId === identity.userId
      && document?.uid.trim() === identity.workflowId
    );
  }
}

/** Snapshot of live scenes/parts for {@link validate}. Used by the file model and the raw editor. */
export function buildWorkflowReferenceCatalog(project: IProjectModel): WorkflowReferenceCatalog {
  const scenes = project.scenes.map((scene) => ({
    id: scene.id,
    name: scene.name.trim(),
    index: scene.index,
    loaded: project.isSceneLoaded(scene.id),
  }));
  const partsByScene = new Map<string, WorkflowScenePartIndex>();
  for (const scene of project.scenes) {
    if (!project.isSceneLoaded(scene.id)) continue;
    const key = scene.id.toLowerCase();
    if (key.length === 0) continue;
    const entries = buildDisplayNamePathIndex(project.getEntities(scene.id));
    partsByScene.set(key, {
      displayPaths: new Set(entries.map((entry) => entry.namePath.toLowerCase())),
      indexPaths: new Set(entries.map((entry) => entry.indexPath)),
    });
  }
  return { scenes, partsByScene };
}

function uniqueWorkflowKnowledgeDocumentKeys(
  document: WorkflowDocument | null | undefined,
): string[] {
  if (!document) return [];
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const entry of document.knowledgeDocs) {
    if (entry.isInvalid) continue;
    const key = normalizeProductNameKey(entry.doc);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
  }
  return keys;
}
